/**
 * Componente SkeletonLoader - Cargador de Esqueleto Reutilizable
 * Reemplaza CircularProgress con skeletons más informativos
 * ESTÁNDAR: Usar en toda la aplicación para estados de carga
 */

import React from 'react';
import {
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

interface SkeletonLoaderProps {
  /**
   * Tipo de skeleton a mostrar
   */
  variant?: 'table' | 'list' | 'card' | 'text';
  
  /**
   * Número de elementos a mostrar
   */
  count?: number;
  
  /**
   * Si es true, muestra un skeleton de tabla
   */
  showTable?: boolean;
  
  /**
   * Número de columnas en la tabla
   */
  columns?: number;
  
  /**
   * Estilos personalizados
   */
  sx?: SxProps<Theme>;
}

export const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  variant = 'list',
  count = 5,
  showTable = false,
  columns = 5,
  sx,
}) => {
  const theme = useTheme();
  
  if (showTable || variant === 'table') {
    return (
      <TableContainer component={Paper} sx={{ borderRadius: 2, ...sx }}>
        <Table>
          <TableHead>
            <TableRow>
              {Array.from({ length: columns }).map((_, index) => (
                <TableCell key={index}>
                  <Skeleton variant="text" width="60%" height={19.2} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: count }).map((_, rowIndex) => (
              <TableRow key={rowIndex}>
                {Array.from({ length: columns }).map((_, colIndex) => (
                  <TableCell key={colIndex}>
                    <Skeleton
                      variant="text"
                      width={colIndex === 0 ? '80%' : '60%'}
                      height={16}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }
  
  if (variant === 'card') {
    return (
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', ...sx }}>
        {Array.from({ length: count }).map((_, index) => (
          <Paper
            key={index}
            sx={{
              p: 2,
              width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.333% - 11px)' },
              borderRadius: 2,
            }}
          >
            <Skeleton variant="rectangular" width="100%" height={96} sx={{ mb: 2, borderRadius: 1 }} />
            <Skeleton variant="text" width="80%" height={19.2} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="60%" height={16} />
          </Paper>
        ))}
      </Box>
    );
  }
  
  if (variant === 'text') {
    return (
      <Box sx={{ ...sx }}>
        {Array.from({ length: count }).map((_, index) => (
          <Skeleton
            key={index}
            variant="text"
            width={index === 0 ? '100%' : index === count - 1 ? '60%' : '80%'}
            height={16}
            sx={{ mb: index < count - 1 ? 1 : 0 }}
          />
        ))}
      </Box>
    );
  }
  
  // Variante 'list' por defecto
  return (
    <Box sx={{ ...sx }}>
      {Array.from({ length: count }).map((_, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 1.5,
            borderBottom: index < count - 1 ? `1px solid ${theme.palette.divider}` : 'none',
          }}
        >
          <Skeleton variant="circular" width={40} height={40} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" height={24} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="40%" height={20} />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

